import { useState, useEffect } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import { COMPLIANCE_EMAIL } from "../lib/brand";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import {
  ShieldCheck,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  Clock,
  RefreshCw,
  Stamp,
  FileDown,
} from "lucide-react";

/**
 * Admin · Security Posture Dashboard
 * Green/yellow/red snapshot of the platform's cybersecurity controls.
 * Intended as a single screen to show insurers, partners, regulators.
 */
const STATUS_META = {
  green:  { Icon: CheckCircle2,  text: "text-emerald-400", bg: "bg-emerald-500/10",  border: "border-emerald-500/40", chip: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", label: "ACTIVE" },
  yellow: { Icon: AlertTriangle, text: "text-amber-400",   bg: "bg-amber-500/10",    border: "border-amber-500/40",   chip: "bg-amber-500/20 text-amber-300 border-amber-500/40",     label: "REVIEW" },
  red:    { Icon: AlertOctagon,  text: "text-rose-400",    bg: "bg-rose-500/10",     border: "border-rose-500/40",    chip: "bg-rose-500/20 text-rose-300 border-rose-500/40",        label: "ATTENTION" },
};

const formatDateTime = (iso) => {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const SecurityStatusPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stamping, setStamping] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/security-status");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load security posture");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markReviewed = async () => {
    setStamping(true);
    try {
      const r = await api.post("/admin/security-status/mark-reviewed");
      toast.success(`Security posture marked reviewed by ${r.data.reviewer}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stamp review");
    } finally {
      setStamping(false);
    }
  };

  /**
   * Export a one-page formal PDF — branded header, posture summary,
   * 13-control table, reviewer stamp, disclaimer footer. Renders text
   * primitives (NOT a canvas screenshot) so output is sharp at any zoom
   * and small in file size (~10-15 KB).
   */
  const exportPdf = () => {
    if (!data) return;
    setExporting(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter" }); // 612 × 792 pt
      const W = doc.internal.pageSize.getWidth();
      let y = 50;

      // Brand header
      doc.setFont("helvetica", "bold");
      doc.setTextColor(11, 18, 32); // navy
      doc.setFontSize(20);
      doc.text("EchoSense Capital Club", 50, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text("Cybersecurity Posture Report", 50, y + 14);
      // Right-aligned date
      doc.setFontSize(9);
      doc.text(new Date().toLocaleString(), W - 50, y, { align: "right" });
      y += 38;

      // Divider
      doc.setDrawColor(234, 179, 8); // gold
      doc.setLineWidth(1.5);
      doc.line(50, y, W - 50, y);
      y += 24;

      // Overall headline
      const overallColor = data.overall === "green"
        ? [16, 185, 129]
        : data.overall === "yellow"
          ? [245, 158, 11]
          : [244, 63, 94];
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(120, 120, 120);
      doc.text("OVERALL POSTURE", 50, y);
      doc.setFontSize(28);
      doc.setTextColor(...overallColor);
      doc.text(data.overall.toUpperCase(), 50, y + 30);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      doc.text(data.headline, 50, y + 50);
      y += 70;

      // Counts strip
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      const { counts } = data;
      doc.text(
        `${counts.green} active   •   ${counts.yellow} review   •   ${counts.red} attention   •   ${counts.total} controls checked`,
        50, y
      );
      y += 24;

      // Table header
      doc.setFillColor(11, 18, 32);
      doc.rect(50, y, W - 100, 24, "F");
      doc.setTextColor(234, 179, 8);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("CONTROL", 60, y + 16);
      doc.text("STATUS", 290, y + 16);
      doc.text("NOTES", 360, y + 16);
      y += 30;

      // Control rows
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const rowHeight = 38;
      data.controls.forEach((c, idx) => {
        if (y > 720) { doc.addPage(); y = 50; }

        // Alternating row tint
        if (idx % 2 === 1) {
          doc.setFillColor(245, 245, 245);
          doc.rect(50, y - 12, W - 100, rowHeight, "F");
        }

        // Status pill
        const pillColor = c.status === "green"
          ? [16, 185, 129]
          : c.status === "yellow"
            ? [245, 158, 11]
            : [244, 63, 94];
        const statusLabel = c.status === "green" ? "ACTIVE" : c.status === "yellow" ? "REVIEW" : "ATTENTION";
        doc.setFillColor(...pillColor);
        doc.roundedRect(290, y - 2, 55, 14, 3, 3, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text(statusLabel, 317.5, y + 7, { align: "center" });

        // Control label
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(11, 18, 32);
        const labelLines = doc.splitTextToSize(c.label, 220);
        doc.text(labelLines, 60, y + 6);

        // Note (wrapped)
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(80, 80, 80);
        const noteLines = doc.splitTextToSize(c.note, 200);
        doc.text(noteLines.slice(0, 3), 360, y + 6); // cap at 3 lines per row

        y += rowHeight;
      });

      // Reviewer + disclaimer footer
      if (y > 660) { doc.addPage(); y = 50; }
      y += 12;
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.line(50, y, W - 50, y);
      y += 18;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(11, 18, 32);
      doc.text("Last Reviewed:", 50, y);
      doc.setFont("helvetica", "normal");
      doc.text(
        data.last_reviewed_at
          ? `${new Date(data.last_reviewed_at).toLocaleString()}${data.last_reviewed_by ? "  •  by " + data.last_reviewed_by : ""}`
          : "Never",
        130, y
      );
      y += 16;

      doc.setFont("helvetica", "bold");
      doc.text("Generated By:", 50, y);
      doc.setFont("helvetica", "normal");
      doc.text(user?.email || "—", 130, y);
      y += 24;

      // Disclaimer
      doc.setFont("helvetica", "italic");
      doc.setFontSize(7.5);
      doc.setTextColor(120, 120, 120);
      const disclaimer = doc.splitTextToSize(
        "This report reflects cybersecurity controls verifiable from the running platform environment at the time of generation. " +
        "It is not a substitute for a third-party penetration test or formal SOC 2 / ISO 27001 attestation. " +
        "Statements are intentionally factual and avoid absolute claims such as \"bank-grade\" or \"unhackable\". " +
        `For formal external attestation, contact ${COMPLIANCE_EMAIL}.`,
        W - 100
      );
      doc.text(disclaimer, 50, y);

      const filename = `echosense-security-posture-${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(filename);
      toast.success(`Exported ${filename}`);
    } catch (e) {
      console.error(e);
      toast.error("Could not export PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Security Posture" subtitle="Loading…">
        <div className="h-48 rounded-lg bg-navy-light/40 animate-pulse" data-testid="security-status-loading" />
      </DashboardLayout>
    );
  }
  if (!data) return null;

  const overall = STATUS_META[data.overall] || STATUS_META.yellow;
  const { counts, controls } = data;

  return (
    <DashboardLayout
      title="Security Posture"
      subtitle="Green/yellow/red snapshot of platform cybersecurity controls"
    >
      {/* Overall card */}
      <Card
        className={`mb-6 ${overall.bg} ${overall.border} border-2`}
        data-testid="security-overall-card"
      >
        <CardContent className="p-6 flex flex-col md:flex-row md:items-center gap-5">
          <div className={`w-16 h-16 rounded-full border-2 ${overall.border} ${overall.bg} flex items-center justify-center shrink-0`}>
            <overall.Icon className={overall.text} size={32} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] uppercase tracking-[0.3em] font-semibold ${overall.text}`}>
              Overall Security Posture
            </p>
            <h2 className="text-2xl font-serif text-cream mt-1">
              <span className={overall.text} data-testid="security-overall-label">
                {data.overall.toUpperCase()}
              </span>
              <span className="text-platinum/70 text-base font-sans ml-3">· {data.headline}</span>
            </h2>
            <p className="text-platinum/60 text-sm mt-2">
              {counts.green} active · {counts.yellow} review · {counts.red} attention ·
              {" "}<span className="text-platinum/40">{counts.total} controls checked</span>
            </p>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
            {/* CAPMARKETS-300: `cream` is a heading ink and turns navy in the light
                theme, so a navy label on it vanished (1.00:1). `surface` flips the
                other way, which keeps this an inverse button in both themes. */}
            <Button
              onClick={exportPdf}
              disabled={exporting}
              size="sm"
              className="bg-cream hover:bg-cream/90 text-surface font-semibold border border-gold/40"
              data-testid="security-export-pdf-btn"
            >
              <FileDown size={14} className="mr-2" />
              {exporting ? "Exporting…" : "Export PDF"}
            </Button>
            <Button
              onClick={load}
              variant="outline"
              size="sm"
              className="border-platinum/25 text-platinum hover:bg-platinum/10 hover:text-cream"
              data-testid="security-refresh-btn"
            >
              <RefreshCw size={14} className="mr-2" /> Refresh
            </Button>
            <Button
              onClick={markReviewed}
              disabled={stamping}
              size="sm"
              className="bg-gold hover:bg-gold/90 text-navy font-semibold"
              data-testid="security-mark-reviewed-btn"
            >
              <Stamp size={14} className="mr-2" />
              {stamping ? "Stamping…" : "Mark Reviewed"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Last review line */}
      <div className="mb-6 flex items-center gap-2 text-platinum/60 text-sm" data-testid="security-last-reviewed">
        <Clock size={14} className="text-gold" />
        Last reviewed:
        <span className="text-cream font-medium">
          {formatDateTime(data.last_reviewed_at)}
        </span>
        {data.last_reviewed_by && (
          <span className="text-platinum/50">· by {data.last_reviewed_by}</span>
        )}
      </div>

      {/* Controls grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {controls.map((c) => {
          const m = STATUS_META[c.status] || STATUS_META.yellow;
          return (
            <Card
              key={c.key}
              className={`${m.bg} ${m.border} border`}
              data-testid={`security-control-${c.key}`}
            >
              <CardContent className="p-5 flex gap-4">
                <div className={`w-10 h-10 rounded-full border ${m.border} ${m.bg} flex items-center justify-center shrink-0`}>
                  <m.Icon className={m.text} size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <h3 className="text-cream font-semibold leading-tight">{c.label}</h3>
                    <Badge className={`${m.chip} border text-[10px] tracking-wider font-semibold shrink-0`}>
                      {m.label}
                    </Badge>
                  </div>
                  <p className="text-platinum/70 text-sm leading-relaxed">{c.note}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Disclaimer footer */}
      <div className="mt-8 p-4 rounded-lg bg-navy-light/40 border border-platinum/10 flex items-start gap-3">
        <ShieldCheck className="text-gold/70 shrink-0 mt-0.5" size={16} />
        <p className="text-platinum/55 text-xs leading-relaxed">
          This dashboard reflects controls verifiable from the running environment. It is not a substitute
          for a third-party penetration test or formal SOC 2 audit. Statements here are intentionally factual
          and avoid absolute claims such as "bank-grade" or "unhackable". For external reviewers requesting
          formal attestation, contact compliance.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default SecurityStatusPage;
